import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  ChevronDown, ChevronUp, ChevronsUpDown, Filter,
  Settings2, Eye, EyeOff, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface DataTableColumn {
  key: string;
  label: string;
  /** Responsive visibility */
  visibility?: {
    mobile?: boolean;   // < 640px
    tablet?: boolean;   // 640-1024px
    desktop?: boolean;  // > 1024px
  };
  /** Column width in px (optional) */
  width?: number;
  /** Max column width in px */
  maxWidth?: number;
  /** Sortable */
  sortable?: boolean;
  /** Align */
  align?: 'left' | 'center' | 'right';
  /** Format value */
  format?: 'number' | 'currency' | 'percent' | 'date' | 'boolean' | 'string';
  /** Aggregation for footer */
  aggregate?: 'sum' | 'avg' | 'count' | 'min' | 'max' | 'none';
  /** Custom render */
  render?: (value: unknown, row: Record<string, unknown>) => React.ReactNode;
}

export interface DataTableFilter {
  key: string;
  type: 'text' | 'select' | 'number' | 'date';
  label: string;
  options?: Array<{ value: string; label: string }>;
  value?: unknown;
}

export interface DataTableAction {
  /** Иконка (Lucide icon name) */
  icon?: string;
  /** Текст кнопки */
  label: string;
  /** Обработчик */
  onClick: (selectedRows: Record<string, unknown>[]) => void;
  /** Показывать только при выделении строк */
  requiresSelection?: boolean;
}

export interface DataTableProps {
  columns: DataTableColumn[];
  data: Record<string, unknown>[];
  /** Row key field */
  rowKey?: string;
  /** Filters */
  filters?: DataTableFilter[];
  onFilterChange?: (filters: Record<string, unknown>) => void;
  /** Pagination */
  pageSize?: number;
  totalRows?: number;
  onLoadMore?: () => void;
  loading?: boolean;
  /** Footer */
  showFooter?: boolean;
  /** Sorting */
  sortable?: boolean;
  /** Striped rows */
  striped?: boolean;
  /** Compact mode */
  compact?: boolean;
  /** Caption */
  caption?: string;
  /** Empty state */
  emptyText?: string;
  /** Row click */
  onRowClick?: (row: Record<string, unknown>) => void;
  /** Row double click */
  onRowDoubleClick?: (row: Record<string, unknown>) => void;
  /** Row class name function */
  rowClassName?: (row: Record<string, unknown>) => string;
  /** Row inline style function (условное оформление строк — стили) */
  rowStyle?: (row: Record<string, unknown>) => React.CSSProperties;
  /** Multi-select */
  selectable?: boolean;
  /** Выделение строк без чекбоксов — клик по строке переключает выделение */
  noCheckboxes?: boolean;
  selectedKeys?: Set<string | number>;
  onSelectionChange?: (keys: Set<string | number>) => void;
  /** Внешнее управление режимом выделения (из AutoListForm — единый для всех видов) */
  selectionActive?: boolean;
  /** Вызывается при УДЕРЖАНИИ строки (500мс) — внешний код включает режим выделения */
  onLongPress?: () => void;
  /** Скрыть тулбар (фильтры/настройки колонок) — всё выносится в Header */
  hideToolbar?: boolean;
  /** Скрыть строку ПОИСКА (если поиск уже есть снаружи — в ListToolbar) */
  hideSearch?: boolean;
  /** Внешнее управление видимостью фильтров */
  showFiltersExternal?: boolean;
  onShowFiltersExternal?: (v: boolean) => void;
  /** Внешнее управление видимостью колонок */
  columnVisibilityExternal?: Record<string, boolean>;
  onColumnVisibilityExternal?: (v: Record<string, boolean>) => void;
  /** Action bar */
  actionBar?: {
    /** Показывать action bar */
    visible?: boolean;
    /** Кнопка "Создать" */
    showCreate?: boolean;
    onCreate?: () => void;
    /** Кнопка "Копировать" */
    showCopy?: boolean;
    onCopy?: (selectedRows: Record<string, unknown>[]) => void;
    /** Кнопка "Добавить" (для не-query источников) */
    showAdd?: boolean;
    onAdd?: () => void;
    /** Дополнительные команды */
    customActions?: DataTableAction[];
  };
}

// ===== Hooks =====
export function useBreakpoint() {
  const [bp, setBp] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      if (w < 640) setBp('mobile');
      else if (w < 1024) setBp('tablet');
      else setBp('desktop');
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  return bp;
}

// ===== Utility =====
export function aggregateValues(values: unknown[], type: DataTableColumn['aggregate']): string {
  const nums = values
    .map((v) => (typeof v === 'number' ? v : parseFloat(String(v))))
    .filter((n) => !isNaN(n));

  if (nums.length === 0) return '—';

  switch (type) {
    case 'sum': return nums.reduce((a, b) => a + b, 0).toLocaleString('ru-RU');
    case 'avg': return (nums.reduce((a, b) => a + b, 0) / nums.length).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    case 'count': return String(nums.length);
    case 'min': return Math.min(...nums).toLocaleString('ru-RU');
    case 'max': return Math.max(...nums).toLocaleString('ru-RU');
    default: return '—';
  }
}

export function formatValue(value: unknown, format?: DataTableColumn['format']): string {
  if (value == null || value === '') return '—';
  switch (format) {
    case 'number': return Number(value).toLocaleString('ru-RU');
    case 'currency': return Number(value).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' });
    case 'percent': return Number(value).toLocaleString('ru-RU', { style: 'percent', maximumFractionDigits: 1 });
    case 'date': {
      try { return new Date(String(value)).toLocaleDateString('ru-RU'); }
      catch { return String(value); }
    }
    case 'boolean': return value ? '✓' : '✗';
    default: return String(value);
  }
}

// ===== Components =====

/** Sort icon */
export function SortIcon({ direction }: { direction: 'asc' | 'desc' | null }) {
  if (direction === 'asc') return <ChevronUp className="h-3 w-3" />;
  if (direction === 'desc') return <ChevronDown className="h-3 w-3" />;
  return <ChevronsUpDown className="h-3 w-3 opacity-30" />;
}

/** Column settings popover */
export function ColumnSettings({
  columns,
  visibility,
  onToggle,
}: {
  columns: DataTableColumn[];
  visibility: Record<string, boolean>;
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setOpen(!open)}>
        <Settings2 className="h-3.5 w-3.5" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border bg-popover p-2 shadow-md space-y-1">
            <div className="text-xs font-medium text-muted-foreground px-2 py-1">Видимость колонок</div>
            {columns.map((col) => (
              <button
                key={col.key}
                className="flex items-center gap-2 w-full px-2 py-1 text-sm rounded hover:bg-muted transition-colors"
                onClick={() => onToggle(col.key)}
              >
                {visibility[col.key] !== false ? (
                  <Eye className="h-3 w-3 text-primary" />
                ) : (
                  <EyeOff className="h-3 w-3 text-muted-foreground" />
                )}
                <span className={visibility[col.key] !== false ? '' : 'text-muted-foreground'}>
                  {col.label}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Column filter */
export function ColumnFilterInput({
  filter,
  value,
  onChange,
}: {
  filter: DataTableFilter;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  if (filter.type === 'select' && filter.options) {
    return (
      <select
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value || undefined)}
        className="w-full rounded border bg-input px-2 py-1 text-xs"
      >
        <option value="">Все</option>
        {filter.options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }

  return (
    <Input
      type={filter.type === 'date' ? 'date' : filter.type === 'number' ? 'number' : 'text'}
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value || undefined)}
      placeholder={filter.label}
      className="h-7 text-xs"
    />
  );
}

// ===== Main Component =====
